import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { getPatItemCohort } from '../../../../services/analytics/analytics.service'
import Loading from '../../../Loading/Loading'
import DataAsOf from './DataAsOf'

// Cohorte item-level: % de acierto por dimensión (strand / sub-strand / posición).
// Detecta fortalezas y debilidades del grupo. MF-508.
const C_BAR = '#2b5784'

const SCALES = [
  { value: 'pat-maths-scale', label: 'PAT Maths' },
  { value: 'pat-reading-scale', label: 'PAT Reading' }
]

const DIMS = [
  { value: 'strand', label: 'Strand' },
  { value: 'sub_strand', label: 'Sub-strand' },
  { value: 'position', label: 'Position' }
]

// Rojo (débil) → ámbar → verde (fuerte). Hace saltar las áreas flojas.
const pctColor = (v) => {
  if (v == null) return '#cfd6e0'
  if (v < 50) return '#c0392b'
  if (v < 70) return '#e0a100'
  return '#2e9e68'
}

const Toggle = ({ options, value, onChange }) => (
  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
    {options.map((s) => (
      <button
        key={s.value}
        onClick={() => onChange(s.value)}
        style={{
          fontSize: 12,
          fontWeight: 600,
          padding: '5px 11px',
          borderRadius: 7,
          cursor: 'pointer',
          border: '1px solid ' + (value === s.value ? C_BAR : '#d5dbe4'),
          background: value === s.value ? C_BAR : '#fff',
          color: value === s.value ? '#fff' : '#5a6472'
        }}
      >
        {s.label}
      </button>
    ))}
  </div>
)

const buildOption = (rows, dimension) => {
  const byPosition = dimension === 'position'
  const sorted = [...rows]
  if (byPosition) sorted.sort((a, b) => Number(a.dimension) - Number(b.dimension))
  else sorted.sort((a, b) => Number(a.pct_correct || 0) - Number(b.pct_correct || 0))

  const cats = sorted.map((r) => (byPosition ? String(r.dimension) : r.dimension))
  const pct = sorted.map((r) => Number(r.pct_correct || 0))
  const meta = sorted

  const bar = {
    type: 'bar',
    data: pct.map((v) => ({ value: v, itemStyle: { color: pctColor(v) } })),
    label: {
      show: !byPosition,
      position: 'right',
      fontSize: 10,
      color: '#333',
      formatter: (p) => p.value + '%'
    },
    barWidth: byPosition ? undefined : '62%'
  }

  const common = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const p = params[0]
        const m = meta[p.dataIndex] || {}
        return `<b>${byPosition ? 'Position ' + p.name : p.name}</b><br/>
          ${p.value}% correct<br/>
          ${m.correct}/${m.responses} responses · ${m.students} students${
            m.avg_difficulty != null ? '<br/>avg difficulty ' + m.avg_difficulty : ''
          }`
      }
    },
    series: [bar]
  }

  if (byPosition) {
    return {
      ...common,
      grid: { left: 46, right: 20, top: 16, bottom: 44 },
      xAxis: {
        type: 'category',
        data: cats,
        name: 'Position within test',
        nameLocation: 'middle',
        nameGap: 26,
        nameTextStyle: { fontSize: 11, color: '#888' },
        axisLabel: { fontSize: 10, color: '#aaa' },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        max: 100,
        axisLabel: { fontSize: 10, color: '#aaa', formatter: '{value}%' },
        splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
      }
    }
  }

  return {
    ...common,
    grid: { left: 150, right: 48, top: 10, bottom: 30 },
    xAxis: {
      type: 'value',
      max: 100,
      name: '% correct',
      nameLocation: 'middle',
      nameGap: 26,
      nameTextStyle: { fontSize: 11, color: '#888' },
      axisLabel: { fontSize: 10, color: '#aaa', formatter: '{value}%' },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    yAxis: {
      type: 'category',
      data: cats,
      axisLabel: { fontSize: 11, color: '#555' },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e0e0e0' } }
    }
  }
}

const PatItemCohort = () => {
  const [scale, setScale] = useState('pat-maths-scale')
  const [dimension, setDimension] = useState('strand')
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLoading(true)
    getPatItemCohort({ scaleSlug: scale, dimension })
      .then((res) => setData(res?.data || []))
      .catch((err) => setError(err?.message || 'Error loading item cohort'))
      .finally(() => setLoading(false))
  }, [scale, dimension])

  const option = useMemo(() => buildOption(data, dimension), [data, dimension])

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap'
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: C_BAR }}>Cohort item performance</h2>
          <DataAsOf />
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Toggle options={DIMS} value={dimension} onChange={setDimension} />
          <Toggle options={SCALES} value={scale} onChange={setScale} />
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <div style={{ padding: 24, color: '#c0392b' }}>
          <strong>Error:</strong> {error}
        </div>
      ) : !data.length ? (
        <p style={{ color: '#888', padding: 24 }}>No item-level data yet — run a sync and ingest first.</p>
      ) : (
        <ReactECharts
          option={option}
          style={{
            height: dimension === 'position' ? 360 : Math.max(320, data.length * 30),
            width: '100%'
          }}
          opts={{ renderer: 'canvas' }}
          notMerge
        />
      )}
    </div>
  )
}

export default PatItemCohort
